import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CloseIcon from '@mui/icons-material/Close';
import useSwipe from '../hooks/useSwipe';

const fadeIn = keyframes`from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}`;

const ModalWrap = styled(Box)({
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(0,0,0,0.92)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
  overflowY: 'auto',
  animation: `${fadeIn} 0.2s ease`,
});

const SKIP_AFTER_SECONDS = 60;

const CamEmbedModal = ({ performer, allPerformers = [], onClose, onNext }) => {
  const [current, setCurrent]     = useState(performer);
  const [countdown, setCountdown] = useState(SKIP_AFTER_SECONDS);
  const [showNext, setShowNext]   = useState(false);
  const [streamStatus, setStreamStatus] = useState('loading'); // loading | live | private | offline
  const timerRef = useRef(null);
  const countRef = useRef(null);
  const iframeRef = useRef(null);
  const loadTimeoutRef = useRef(null);

  const currentIndex = allPerformers.findIndex(p => p.uniqueModelId && p.uniqueModelId === current?.uniqueModelId);
  // Only show next if there's actually a different performer to go to
  const nextPerformer = allPerformers.length > 1 ? (allPerformers[currentIndex + 1] || allPerformers[0]) : null;

  const goNext = useCallback(() => {
    if (!nextPerformer) return;
    clearInterval(timerRef.current);
    clearInterval(countRef.current);
    clearTimeout(loadTimeoutRef.current);
    // If onNext provided (profile page), navigate there instead of switching in-modal
    if (onNext) {
      onNext(nextPerformer);
      return;
    }
    setCurrent(nextPerformer);
    setCountdown(SKIP_AFTER_SECONDS);
    setShowNext(false);
    setStreamStatus('loading');
  }, [nextPerformer, onNext]);

  // Listen for postMessage from Streamate iframe (they sometimes broadcast status)
  useEffect(() => {
    const handleMessage = (e) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        // Streamate/Naiads may send status events when performer goes private or offline
        if (data.status === 'private' || data.event === 'private' || data.type === 'private_show' ||
            data.status === 'offline' || data.event === 'offline' || data.event === 'away') {
          setStreamStatus('private');
        }
      } catch (err) {
        // Not JSON or not relevant — ignore
        const msg = String(e.data || '').toLowerCase();
        if (msg.includes('private') || msg.includes('offline') || msg.includes('away')) {
          setStreamStatus('private');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Mark stream as live after iframe loads, set status to loading on switch
  const handleIframeLoad = useCallback(() => {
    clearTimeout(loadTimeoutRef.current);
    setStreamStatus('live');
  }, []);

  // Start 5s load timeout whenever performer changes — if still loading, treat as private
  useEffect(() => {
    clearTimeout(loadTimeoutRef.current);
    setStreamStatus('loading');
    loadTimeoutRef.current = setTimeout(() => {
      setStreamStatus(s => s === 'loading' ? 'private' : s);
    }, 5000);
    return () => clearTimeout(loadTimeoutRef.current);
  }, [current]);

  useEffect(() => {
    setCurrent(performer);
    setCountdown(SKIP_AFTER_SECONDS);
    setShowNext(false);
    setStreamStatus('loading');
  }, [performer]);

  useEffect(() => {
    clearInterval(countRef.current);
    countRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(countRef.current);
          setShowNext(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(countRef.current);
  }, [current]);

  // Previous performer (for swipe right)
  const prevPerformer = currentIndex > 0 ? allPerformers[currentIndex - 1] : allPerformers[allPerformers.length - 1];

  const goPrev = useCallback(() => {
    if (!prevPerformer) return;
    clearInterval(timerRef.current);
    clearInterval(countRef.current);
    clearTimeout(loadTimeoutRef.current);
    if (onNext) { onNext(prevPerformer); return; }
    setCurrent(prevPerformer);
    setCountdown(SKIP_AFTER_SECONDS);
    setShowNext(false);
    setStreamStatus('loading');
  }, [prevPerformer, onNext]);

  const swipeHandlers = useSwipe(goNext, goPrev);

  if (!current) return null;

  const embedUrl = current.embedUrl ||
    `https://hybridclient.naiadsystems.com/purecam?performer=${current.name || current.displayName}&widescreen=true&AFNO=1-94033`;
  const chatUrl = current.chatRoomUrl ||
    `https://instacamgirlslive.com/cam/${current.name || current.displayName}/?AFNO=1-94033`;
  const displayName = current.displayName || current.name;

  // Streamate full embed (includes chat) — used when user clicks "Open Full"
  // No standalone Streamate chat-only embed available

  return (
    <ModalWrap>
      {/* Outer container: video + chat side by side on desktop */}
      <Box
        {...swipeHandlers}
        sx={{
          position: 'relative',
          width: { xs: '100vw', md: '90vw' },
          maxWidth: { xs: '100vw', md: 1000 },
          minHeight: { xs: '100dvh', md: 'unset' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          bgcolor: '#0d0d0d',
          borderRadius: { xs: 0, md: 2 },
          overflow: 'hidden',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          mx: 'auto',
        }}>

        {/* Left: video column */}
        <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Top bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: '#111' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } } }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{displayName}</Typography>
              {current.goldShow && (
                <Box sx={{ bgcolor: '#FFD700', color: '#000', px: 1, py: 0.2, borderRadius: 1, fontSize: '0.65rem', fontWeight: 800 }}>
                  🥇 GOLD SHOW
                </Box>
              )}
            </Box>
            <Button onClick={onClose} sx={{ color: '#999', minWidth: 0, p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </Button>
          </Box>

          {/* Video iframe */}
          <Box sx={{ position: 'relative', width: '100%', height: { xs: 'calc(100vw * 9 / 16)', md: 520 }, minHeight: { xs: 'min(60vh, 400px)', md: 280 } }}>
            <iframe
              ref={iframeRef}
              key={current.id || current.uniqueModelId}
              src={embedUrl}
              title={`${displayName} live cam`}
              width="100%" height="100%"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; fullscreen; microphone; camera"
              style={{ display: 'block' }}
              onLoad={handleIframeLoad}
            />

            {/* Performer went private overlay — blurred photo background */}
            {streamStatus === 'private' && (
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, animation: `${fadeIn} 0.3s ease`,
                overflow: 'hidden',
              }}>
                {/* Blurred performer photo background */}
                {(current.profilePictureUrl || current.thumb) && (
                  <Box component="img"
                    src={current.profilePictureUrl || current.thumb}
                    alt=""
                    sx={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'cover', objectPosition: 'top center',
                      filter: 'blur(12px) brightness(0.35)',
                      transform: 'scale(1.1)',
                      zIndex: 0,
                    }}
                  />
                )}
                {!current.profilePictureUrl && !current.thumb && (
                  <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 0 }} />
                )}
                {/* Content on top */}
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ fontSize: 40 }}>🔒</Box>
                  <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                    {displayName} is in a Private Show
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                    She's busy with another viewer right now
                  </Typography>
                  {nextPerformer && (
                    <Button
                      variant="contained"
                      endIcon={<SkipNextIcon />}
                      onClick={goNext}
                      sx={{ background: 'linear-gradient(45deg,#FF6B6B,#FF2BAA)', borderRadius: '20px',
                        px: 3, py: 1, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem',
                        boxShadow: '0 4px 20px rgba(255,65,150,0.5)' }}
                    >
                      Watch {nextPerformer.displayName || nextPerformer.name}
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Next girl overlay — appears after 60s */}
            {showNext && nextPerformer && (
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
                p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>
                  Up next: <strong>{nextPerformer.displayName || nextPerformer.name}</strong>
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<SkipNextIcon />}
                  onClick={goNext}
                  sx={{ background: 'linear-gradient(45deg,#FF6B6B,#FF2BAA)', borderRadius: '20px',
                    fontWeight: 700, textTransform: 'none', animation: `${fadeIn} 0.3s ease` }}
                >
                  Next Girl
                </Button>
              </Box>
            )}

            {/* Countdown ring — bottom right */}
            {!showNext && countdown <= 15 && (
              <Box sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', alignItems: 'center',
                bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '20px', px: 1.5, py: 0.5, gap: 0.5 }}>
                <CircularProgress size={14} thickness={5} variant="determinate"
                  value={(SKIP_AFTER_SECONDS - countdown) / SKIP_AFTER_SECONDS * 100}
                  sx={{ color: '#FF4196' }} />
                <Typography sx={{ color: '#fff', fontSize: '0.7rem' }}>next in {countdown}s</Typography>
              </Box>
            )}
          </Box>

          {/* Bottom CTA */}
          <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, pb: { xs: '72px', md: 1.5 }, flexWrap: 'wrap', justifyContent: 'center', bgcolor: '#111' }}>
            <Button
              variant="contained"
              startIcon={<LiveTvIcon />}
              sx={{ background: 'linear-gradient(45deg,#e91e63,#9c27b0)', borderRadius: '50px',
                px: 3, fontWeight: 700, textTransform: 'none' }}
              onClick={() => window.open(chatUrl, '_blank')}
            >
              Start Private Show
            </Button>
            {nextPerformer && (
              <Button
                variant="outlined"
                endIcon={<SkipNextIcon />}
                onClick={goNext}
                sx={{ borderColor: '#555', color: '#ccc', borderRadius: '50px',
                  px: 2, fontWeight: 600, textTransform: 'none' }}
              >
                Next: {nextPerformer.displayName || nextPerformer.name}
              </Button>
            )}
          </Box>
        </Box>

        {/* Mobile swipe hint — shows on first open, fades after 2s */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'absolute', bottom: 80, left: 0, right: 0,
          justifyContent: 'center', pointerEvents: 'none', zIndex: 10002,
          animation: 'fadeOutHint 2s ease 1.5s forwards',
          '@keyframes fadeOutHint': { '0%': { opacity: 0.8 }, '100%': { opacity: 0 } },
        }}>
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '20px', px: 2, py: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#fff', fontSize: '0.75rem' }}>← swipe to browse →</Typography>
          </Box>
        </Box>

        {/* Mobile floating Next button — always visible at bottom of screen */}
        {nextPerformer && (
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10001,
            p: 1.5, gap: 1,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.95))',
            backdropFilter: 'blur(4px)',
          }}>
            <Button
              variant="contained"
              fullWidth
              endIcon={<SkipNextIcon />}
              onClick={goNext}
              sx={{
                background: 'linear-gradient(45deg,#FF6B6B,#FF2BAA)',
                borderRadius: '50px', fontWeight: 700, textTransform: 'none',
                py: 1.2, fontSize: '0.95rem',
                boxShadow: '0 4px 20px rgba(255,65,150,0.5)',
              }}
            >
              Next: {nextPerformer.displayName || nextPerformer.name}
            </Button>
          </Box>
        )}

        {/* Right: action panel — desktop only */}
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: 240,
          flexShrink: 0,
          borderLeft: '1px solid #1e1e1e',
          bgcolor: '#0a0a0a',
          p: 2,
          gap: 1.5,
          justifyContent: 'center',
          alignItems: 'stretch',
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem', mb: 1 }}>
            {displayName}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={() => window.open(chatUrl, '_blank')}
            sx={{
              background: 'linear-gradient(135deg,#FF4196,#e91e63)',
              borderRadius: '50px', fontWeight: 700, textTransform: 'none', py: 1.2,
              '&:hover': { background: 'linear-gradient(135deg,#e91e63,#c2185b)' },
            }}
          >
            💬 Open Live Chat
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => window.open(chatUrl, '_blank')}
            sx={{
              borderColor: '#FF4196', color: '#FF4196', borderWidth: 2,
              borderRadius: '50px', fontWeight: 700, textTransform: 'none', py: 1,
              '&:hover': { bgcolor: '#FF4196', color: '#fff', borderColor: '#FF4196' },
            }}
          >
            🔒 Start Private Show
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(current.name || current.displayName)}/?AFNO=1-70001`, '_blank')}
            sx={{
              color: '#888', borderRadius: '50px', fontWeight: 600,
              textTransform: 'none', fontSize: '0.82rem',
              '&:hover': { color: '#fff' },
            }}
          >
            📸 Photos &amp; Videos
          </Button>

          {nextPerformer && (
            <Button
              variant="text"
              fullWidth
              onClick={goNext}
              sx={{
                color: '#555', borderRadius: '50px', fontWeight: 600,
                textTransform: 'none', fontSize: '0.8rem', mt: 'auto',
                '&:hover': { color: '#fff' },
              }}
            >
              Next: {nextPerformer.displayName || nextPerformer.name} ▶
            </Button>
          )}
        </Box>

      </Box>
    </ModalWrap>
  );
};

export default CamEmbedModal;
